import { Button, Callout, Card, EmptyState, LoadingRegion, Skeleton } from '@apovisio/ui';
import type { MeetingSummary } from '@apovisio/contracts';
import { CalendarClock, Plus, RefreshCw, Video } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { PageHeading } from '../../components/PageHeading';
import { useMeetings } from '../../features/meetings/api';
import { MeetingStatusBadge, OptionBadges } from '../../features/meetings/badges';
import { formatSlot } from '../../lib/format';

function MeetingRows({ meetings }: { meetings: MeetingSummary[] }) {
  return (
    <ul className="app-list">
      {meetings.map((meeting) => (
        <li key={meeting.id}>
          <div className="av-stack av-stack--sm">
            <Link to={`/admin/reunions/${meeting.id}`} className="app-list__title">
              {meeting.title}
            </Link>
            <span className="av-row av-text-secondary av-text-sm">
              <CalendarClock className="av-icon" aria-hidden="true" />
              {formatSlot(meeting.scheduledStart, meeting.scheduledEnd)} ·{' '}
              {meeting.invitationCounts.active} invitation
              {meeting.invitationCounts.active > 1 ? 's' : ''} active
              {meeting.invitationCounts.active > 1 ? 's' : ''}
              {meeting.myRole === 'CO_HOST' ? ' · co-hôte' : ''}
            </span>
          </div>
          <div className="av-row">
            <MeetingStatusBadge status={meeting.status} />
            <OptionBadges chat={meeting.chatEnabled} screenShare={meeting.screenShareEnabled} />
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Écran 3 — Tableau de bord : réunions actives et récentes. */
export function DashboardPage() {
  const meetings = useMeetings();
  const navigate = useNavigate();
  const create = () => {
    void navigate('/admin/reunions/nouvelle');
  };
  const empty = meetings.data?.active.length === 0 && meetings.data.recent.length === 0;

  return (
    <div className="av-stack av-stack--lg">
      <div className="av-row av-row--between">
        <PageHeading documentTitle="Visios">Visios</PageHeading>
        {empty ? null : (
          <Button icon={<Plus />} onClick={create}>
            Créer une visio
          </Button>
        )}
      </div>

      {meetings.isPending ? (
        <Card title="En cours et à venir">
          <LoadingRegion label="Chargement des visios">
            <div className="av-stack">
              {[0, 1, 2].map((i) => (
                <div key={i} className="av-stack av-stack--sm">
                  <Skeleton width="45%" height={18} />
                  <Skeleton width="70%" height={14} />
                </div>
              ))}
            </div>
          </LoadingRegion>
        </Card>
      ) : null}

      {meetings.isError ? (
        <Callout tone="danger" title="Impossible de charger les visios" live="alert">
          <div className="av-stack av-stack--sm">
            <p>Vérifiez votre connexion puis réessayez.</p>
            <div>
              <Button
                variant="secondary"
                icon={<RefreshCw />}
                onClick={() => {
                  void meetings.refetch();
                }}
              >
                Réessayer
              </Button>
            </div>
          </div>
        </Callout>
      ) : null}

      {empty ? (
        <Card as="div">
          <EmptyState
            icon={<Video />}
            title="Aucune visio pour l’instant"
            description="Créez une visio, puis générez un lien privé pour chaque invité."
            action={
              <Button icon={<Plus />} onClick={create}>
                Créer une visio
              </Button>
            }
          />
        </Card>
      ) : null}

      {meetings.data && !empty ? (
        <>
          <Card title="En cours et à venir">
            {meetings.data.active.length > 0 ? (
              <MeetingRows meetings={meetings.data.active} />
            ) : (
              <p className="av-text-secondary">Aucune visio programmée.</p>
            )}
          </Card>
          {meetings.data.recent.length > 0 ? (
            <Card title="Récentes">
              <MeetingRows meetings={meetings.data.recent} />
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
